/**
 * HomeGoalsPreview — Calm "Your Goals" section for the dashboard.
 * Shows top 3 goals as summary cards + a full-width "Browse all" CTA
 * and a scrollable category chip row that deep-links into /goals?category=…
 */
import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useGoalState } from "@/hooks/goal-state";
import { CompactGoalCard } from "@/components/goals/CompactGoalCard";
import { ArrowRight, Target, Plus, LayoutGrid } from "lucide-react";
import { Button } from "@/components/ui/button";
import { parseISO, isValid } from "date-fns";
import { useAuth } from "@/hooks/useAuth";

const TOP_N = 3;

interface Props {
  onCreateGoal?: () => void;
}

export function HomeGoalsPreview({ onCreateGoal }: Props) {
  const { goals, handleDeleteGoal } = useGoalState();
  const { user } = useAuth();
  const navigate = useNavigate();

  const activeGoals = useMemo(() => {
    const active = (goals || []).filter(
      (g) => !g.completedAt && (g.progress ?? 0) < 100
    );
    const timeOf = (d?: string | Date | null) => {
      if (!d) return Number.POSITIVE_INFINITY;
      const parsed = typeof d === "string" ? parseISO(d) : d;
      return isValid(parsed) ? parsed.getTime() : Number.POSITIVE_INFINITY;
    };
    const rank = (g: (typeof active)[number]) => {
      const p = g.progress ?? 0;
      if (p > 0) return 0;
      return 1;
    };
    return [...active].sort((a, b) => {
      const r = rank(a) - rank(b);
      if (r !== 0) return r;
      return timeOf(a.dueDate) - timeOf(b.dueDate);
    });
  }, [goals]);

  // Distinct categories from the user's own goals (any status),
  // sorted by frequency so the most-used sits first.
  const userCategories = useMemo(() => {
    const counts = new Map<string, number>();
    (goals || []).forEach((g) => {
      if (!g.category) return;
      counts.set(g.category, (counts.get(g.category) ?? 0) + 1);
    });
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name]) => name);
  }, [goals]);

  const top = activeGoals.slice(0, TOP_N);
  const totalActive = activeGoals.length;

  const goToGoals = (category?: string) => {
    const qs = category ? `?category=${encodeURIComponent(category)}` : "";
    navigate(`/goals${qs}`);
  };

  return (
    <section aria-label="Your goals" className="space-y-4">
      <p className="gd-kicker flex items-center gap-2">
        <span aria-hidden className="h-px w-6 bg-primary/50" />
        Goals in motion
      </p>
      <div className="flex items-end justify-between gap-3">
        <div className="flex items-end gap-3">
          <span className="mb-0.5 flex h-9 w-9 items-center justify-center rounded-2xl gd-well text-primary">
            <Target className="h-4 w-4" />
          </span>
          <h2 className="font-outfit text-2xl sm:text-[28px] font-bold leading-tight tracking-tight text-foreground">
            Your goals
          </h2>
          {user && totalActive > 0 && (
            <span className="mb-1 font-mono text-xs text-muted-foreground tabular-nums">
              {totalActive} active
            </span>
          )}
        </div>
        {user && onCreateGoal && (
          <Button
            size="sm"
            variant="ghost"
            onClick={onCreateGoal}
            className="h-8 rounded-full px-3 gap-1 text-xs active:scale-[0.97]"
          >
            <Plus className="h-3.5 w-3.5" />
            New
          </Button>
        )}
      </div>

      {/* Category chips → deep link into /goals */}
      {user && userCategories.length > 0 && (
        <div
          className="flex gap-2 overflow-x-auto pb-1 -mx-2 px-2 scrollbar-none"
          role="group"
          aria-label="Jump to goals by category"
        >
          <button
            type="button"
            onClick={() => goToGoals()}
            style={{ touchAction: "manipulation" }}
            className="shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border bg-foreground text-background border-foreground hover:opacity-90 transition"
          >
            All
          </button>
          {userCategories.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => goToGoals(c)}
              style={{ touchAction: "manipulation" }}
              className="shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border bg-background text-foreground border-border hover:bg-muted transition whitespace-nowrap"
            >
              {c}
            </button>
          ))}
        </div>
      )}

      {top.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-muted/20 px-4 py-6 text-center space-y-3">
          <p className="text-sm text-muted-foreground">
            No active goals yet. Create your first one to start tracking progress.
          </p>
          {onCreateGoal && (
            <Button size="sm" onClick={onCreateGoal} className="gap-1.5">
              <Plus className="h-4 w-4" />
              Create a goal
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {top.map((g) => (
            <CompactGoalCard key={g.id} goal={g} onDelete={user ? (goal) => handleDeleteGoal(goal) : undefined} />
          ))}

          {/* Full-width "browse all" footer button — impossible to miss */}
          {user && (
            <Link
              to="/goals"
              className="flex items-center justify-center gap-2 w-full rounded-2xl border border-border bg-card hover:bg-accent transition-colors py-3 text-sm font-semibold text-primary shadow-sm hover:shadow-md active:scale-[0.99]"
              style={{ touchAction: "manipulation" }}
            >
              <LayoutGrid className="h-4 w-4" />
              Browse all goals by category
              {totalActive > TOP_N && (
                <span className="tabular-nums text-muted-foreground">
                  ({totalActive})
                </span>
              )}
              <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </div>
      )}
    </section>
  );
}
